import { useState, useEffect, useRef } from 'react';
import { AlbumArt } from './AlbumArt';
import { SpotifyIcon } from './icons';
import { formatMs } from '../../utils/formatMs';

const spotifyUrl = (uri) => `https://open.spotify.com/track/${uri?.split(':')[2] ?? ''}`;

export function LiveDot() {
  return (
    <span className="relative flex h-2 w-2">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-felt opacity-60
                       motion-reduce:animate-none" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-felt" />
    </span>
  );
}

function Playing({ track }) {
  const [progress, setProgress] = useState(track.progressMs);
  const startRef = useRef(Date.now());

  useEffect(() => {
    setProgress(track.progressMs);
    startRef.current = Date.now();
  }, [track.uri, track.progressMs]);

  useEffect(() => {
    const id = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      setProgress(Math.min(track.progressMs + elapsed, track.durationMs));
    }, 1000);
    return () => clearInterval(id);
  }, [track.uri, track.progressMs, track.durationMs]);

  // ponytail: el progreso se estima desde el poll; si pasó la duración (pausa o tema trabado)
  // se oculta en vez de dejar la barra llena en "-0:00" hasta que cambie el tema.
  const ended = track.durationMs > 0 && progress >= track.durationMs;
  const pct = track.durationMs > 0 ? Math.min((progress / track.durationMs) * 100, 100) : 0;

  return (
    <>
      {/* Halo ambiental: la portada desenfocada queda detrás, nunca encima */}
      {/* Móvil: portada chica para que "Elige tu canción" entre en la primera pantalla */}
      <div className="relative isolate w-[46%] max-w-[180px] lg:w-full lg:max-w-none">
        {track.image && (
          <img
            src={track.image}
            alt=""
            aria-hidden
            className="pointer-events-none absolute inset-0 -z-10 h-full w-full translate-y-3 scale-95
                       object-contain opacity-35 blur-[48px]"
          />
        )}
        <AlbumArt image={track.image} alt={`Portada de ${track.name}`} className="w-full" />
      </div>

      <div className="mt-5 flex w-full lg:mt-7 flex-col items-center text-center lg:items-start lg:text-left">
        <h1 className="font-serif text-[24px] font-semibold leading-tight tracking-tight text-lounge-text
                       lg:text-[40px] lg:leading-[1.1]">
          {track.name}
        </h1>
        <p className="mt-1.5 font-inter text-base text-lounge-stone lg:text-lg">{track.artist}</p>
        <a
          href={spotifyUrl(track.uri)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex min-h-[44px] items-center gap-2 font-mono text-[11px] uppercase
                     tracking-[0.15em] text-lounge-stone transition-colors hover:text-lounge-text"
        >
          <SpotifyIcon className="h-4 w-4 text-[#1DB954]" />
          Abrir en Spotify
        </a>
      </div>

      {!ended && <div className="mt-3 w-full">
        <div
          role="progressbar"
          aria-label="Progreso de la canción"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pct)}
          className="h-1 w-full overflow-hidden rounded-full bg-lounge-raised"
        >
          <div
            className="h-full rounded-full bg-lounge-brass shadow-brass transition-[width] duration-1000 ease-linear
                       motion-reduce:transition-none"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="mt-2 flex justify-between font-mono text-[11px] tabular-nums text-lounge-stone">
          <span>{formatMs(progress)}</span>
          <span>-{formatMs(track.durationMs - progress)}</span>
        </div>
      </div>}
    </>
  );
}

export function NowPlaying({ track }) {
  return (
    <section aria-label="Sonando ahora" className="flex flex-col items-center lg:items-start">
      {track ? (
        <Playing track={track} />
      ) : (
        <div className="flex w-full flex-col items-center rounded-lg border border-lounge-line bg-lounge-chassis
                        px-6 py-12 text-center">
          <p className="font-serif text-xl italic text-lounge-text">La sala está en silencio</p>
          <p className="mt-2 font-inter text-sm text-lounge-stone">
            Elige una canción y sé quien abra la noche.
          </p>
        </div>
      )}
    </section>
  );
}
